import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import Product, {
  Collection,
  UIBlockElement
} from 'common-ts/dist/models/Product';
import { ProductsOnlyList } from 'common-ts/dist/models/Product';
import { fetchProductsList } from './ProductListRedux';
import { publishToWebsite } from './WebsiteStatusRedux';
import { useAppSelector } from 'src/reduxhooks';
import { enableMapSet } from 'immer';
import { cloneElement } from 'react';

enableMapSet();

function deepCopy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// Sementara dibuat di sini dulu interfacenya, mungkin nanti akan dipindah ke Product.ts di common-ts
interface CollectionsListDraft {
  collectionsListDraft: Collection[];
  discoveryCollectionsOrderDraft: string[];
  discoveryCollectionsOrderDraftDict: {[key:string]: string[]};
  editedCollection: Set<string>;
  // True while the general / per-category order drafts differ from the last
  // pushed/fetched state. Drives the "urutan belum di-push" warning.
  orderEdited: boolean;
  // Collections created this session and not yet pushed. Push is blocked until
  // each (non-empty) one is placed in at least one category order (Dict).
  newCollections: Set<string>;
  status: APIRequestStatus;
  statusPostUpdateCollection: APIRequestStatus;
  error: null | Error;
}

const initialState: CollectionsListDraft = {
  collectionsListDraft: [],
  discoveryCollectionsOrderDraft: [],
  discoveryCollectionsOrderDraftDict: {},
  editedCollection: new Set<string>(),
  orderEdited: false,
  newCollections: new Set<string>(),
  status: APIRequestStatus.Idle,
  statusPostUpdateCollection: APIRequestStatus.Idle,
  error: null
};

const compareCollections = (filteredCollections, originalCollections) => {
  const originalCollectionMap = new Map(
    originalCollections.map((c) => [c.collectionId, JSON.stringify(c)])
  );

  console.log(originalCollectionMap)

  return filteredCollections.filter((filteredCollection) => {
    const originalCollectionString = originalCollectionMap.get(
      filteredCollection.collectionId
    );

    return (
      !originalCollectionString ||
      originalCollectionString !== JSON.stringify(filteredCollection)
    );
  });
};

export const postUpdateCollection = createAsyncThunk(
  'collectionUpdate/postUpdateCollection',
  async (arg, thunkAPI) => {
    const state: any = thunkAPI.getState();

    const originalCollectionList = state.collectionList.collectionsList

    // // Hanya koleksi yang diubah yang akan di-push
    // const filteredCollectionsListDraft =
    //   state.collectionUpdate.collectionsListDraft.filter((collection) =>
    //     state.collectionUpdate.editedCollection.has(collection.collectionId)
    //   );

    // Setidaknya daftar koleksi yang akan ditampilkan setiap anggotanya harus punya SALAH SATU (atau ketiganya?) dari ketiga list di dalam objek koleksinya
    const nonEmptyCollectionsListDraft =
      state.collectionUpdate.collectionsListDraft.filter(
        (collection) =>
          collection.discoveryListOfElements.length > 0 ||
          collection.efficientOrderOfItems.length > 0 ||
          collection.productItemsList.length > 0
      );

    const nonEmptyCollectionIds = new Set(
      nonEmptyCollectionsListDraft.map((c) => c.collectionId)
    );

    // Hanya koleksi yang diubah dan mempunyai setidaknya satu anggota di salah satu dari ketiga list di objeknya yang akan di-push
    const filteredCollectionsListDraft =
      state.collectionUpdate.collectionsListDraft.filter(
        (collection) =>
          state.collectionUpdate.editedCollection.has(
            collection.collectionId
          ) && nonEmptyCollectionIds.has(collection.collectionId)
      );

    // Web hanya akan menampilkan koleksi yang mempunyai setidaknya satu anggota di salah satu dari ketiga list di objeknya
    const filteredDiscoveryCollectionsOrderDraft =
      state.collectionUpdate.discoveryCollectionsOrderDraft.filter(
        (collectionId) => nonEmptyCollectionIds.has(collectionId)
      );

      console.log(filteredCollectionsListDraft)

    // Hanya baru atau koleksi yang mengalami perubahan dari list asli yang akan di-push
    const filteredAndComparedCollectionsListDraft = compareCollections(
      filteredCollectionsListDraft,
      originalCollectionList
    );

    const data = {
      collectionsListDraft: filteredAndComparedCollectionsListDraft,
      discoveryCollectionsOrderDraft: filteredDiscoveryCollectionsOrderDraft,
      discoveryCollectionsOrderDictDraft: state.collectionUpdate.discoveryCollectionsOrderDraftDict
    };

    // console.log(data);
    // return {};

    // Save → rebuild the website's product data → wait for its stock data → check the
    // pushed collections are really live (WebsiteStatusRedux). Rejects if the save fails,
    // so the drafts are kept.
    const checkCollectionIds = [
      ...filteredAndComparedCollectionsListDraft.map((c) => c.collectionId),
      ...Array.from(state.collectionUpdate.newCollections as Set<string>)
    ].filter((id, i, all) => all.indexOf(id) === i);
    await thunkAPI.dispatch(publishToWebsite({ collectionsPayload: data, checkCollectionIds })).unwrap();

    return true;
  }
);

export const updateCollectionProductListById = createAsyncThunk(
  'collectionUpdate/updateCollectionProductListById',
  async (arg: string, thunkAPI) => {
    const state: any = thunkAPI.getState();

    const collectionId = arg;

    // Tambah koleksi ke daftar koleksi yang diedit
    const collectionIndex = state.collectionUpdate.collectionsListDraft.findIndex(
      (collection) => collection.collectionId === collectionId
    );
    if (
      collectionIndex === -1
    ) {
      return;
    }

    const discoveryList = state.collectionUpdate.collectionsListDraft[collectionIndex].discoveryListOfElements;

    const newProductDict = {};

    for (let discoveryItem of discoveryList) {
      var contentJsonParsed;
      var curProdId;
      if (discoveryItem?.elementMobileCode && discoveryItem?.elementMobileCode !== 'Empty') {
        const code = discoveryItem.elementMobileCode;
        const contentJson = discoveryItem.elementMobileContentJson;
        if (typeof contentJson === 'string' || contentJson instanceof String) {
          contentJsonParsed = JSON.parse(contentJson.toString());
        } else {
          contentJsonParsed = contentJson;
        }
        switch(code) {
          case 'BlockTwoProductsCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productTwoId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
          case 'BlockThreeProductsCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productTwoId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productThreeId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
          case 'BlockFourProductsCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productTwoId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productThreeId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productFourId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
          case 'BlockOneProductCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
        }
      }
      if (discoveryItem?.elementDesktopCode && discoveryItem?.elementDesktopCode !== 'Empty') {
        const code = discoveryItem.elementDesktopCode;
        const contentJson = discoveryItem.elementDesktopContentJson;
        if (typeof contentJson === 'string' || contentJson instanceof String) {
          contentJsonParsed = JSON.parse(contentJson.toString());
        } else {
          contentJsonParsed = contentJson;
        }
        switch(code) {
          case 'BlockTwoProductsCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productTwoId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
          case 'BlockThreeProductsCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productTwoId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productThreeId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
          case 'BlockFourProductsCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productTwoId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productThreeId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            curProdId = contentJsonParsed?.productFourId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
          case 'BlockOneProductCenter':
            curProdId = contentJsonParsed?.productOneId ?? undefined;
            if (curProdId) {
              newProductDict[curProdId] = true;
            }
            break;
        }
      }
    }

    const newProductList = Object.keys(newProductDict);

    thunkAPI.dispatch(updateCollectionProductList({
      collectionId: collectionId,
      productItemsList: newProductList,
    }));
    thunkAPI.dispatch(updateCollectionEfficientOrderOrItems({
      collectionId: collectionId,
      efficientOrderOfItems: newProductList,
    }));

    return true;
  }
);

export const slice = createSlice({
  name: 'collectionUpdate',
  initialState,
  reducers: {
    // Create Collection
    createCollection(state, action) {
      const newCollection = action.payload;

      console.log(newCollection);

      const existingCollectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === newCollection.collectionId
      );

      if (existingCollectionIndex === -1) {
        state.collectionsListDraft = [
          deepCopy(newCollection),
          ...state.collectionsListDraft
        ];
      }

      const existingOrderIndex = state.discoveryCollectionsOrderDraft.findIndex(
        (collectionId) => collectionId === newCollection.collectionId
      );

      if (existingOrderIndex === -1) {
        state.discoveryCollectionsOrderDraft = [
          newCollection.collectionId,
          ...state.discoveryCollectionsOrderDraft
        ];
      }

      // A new collection is an edit (the push filter only sends collections in
      // editedCollection) and changes the general order; track it as new so the
      // push guard can require a category-order (Dict) placement first.
      state.editedCollection.add(newCollection.collectionId);
      state.newCollections.add(newCollection.collectionId);
      state.orderEdited = true;
    },
    // Reorder Collection
    reorderCollection(state, action) {
      const discoveryCollectionsOrder = action.payload;

      state.discoveryCollectionsOrderDraft = [...discoveryCollectionsOrder];
      state.orderEdited = true;
    },
    // Reorder Collection
    reorderCollectionDict(state, action) {
      const discoveryCollectionsOrderDict = action.payload;

      state.discoveryCollectionsOrderDraftDict = JSON.parse(JSON.stringify(discoveryCollectionsOrderDict));
      state.orderEdited = true;
    },
    // Delete Collection (Soft Delete)
    deleteCollection(state, action) {
      const deletedCollectionId = action.payload;

      state.discoveryCollectionsOrderDraft = [
        ...state.discoveryCollectionsOrderDraft.filter(
          (collectionId) => collectionId !== deletedCollectionId
        )
      ];

      // Keep the per-category order consistent: the Dict is pushed unfiltered,
      // so a deleted id left in it would ship dangling refs to the storefront.
      for (const category of Object.keys(state.discoveryCollectionsOrderDraftDict)) {
        state.discoveryCollectionsOrderDraftDict[category] =
          state.discoveryCollectionsOrderDraftDict[category].filter(
            (collectionId) => collectionId !== deletedCollectionId
          );
      }
      state.orderEdited = true;
    },
    // Update Collection Product List
    updateCollectionProductList(state, action) {
      const collectionId = action.payload.collectionId;
      const productItemsList = action.payload.productItemsList;

      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );

      // Ganti daftar produk apabila koleksi ditemukan di daftar koleksi
      if (collectionIndex !== -1) {
        state.collectionsListDraft[collectionIndex].productItemsList = [
          ...productItemsList
        ];
      }

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    },
    // Update Collection Efficient Order Of Items
    updateCollectionEfficientOrderOrItems(state, action) {
      const collectionId = action.payload.collectionId;
      const efficientOrderOfItems = action.payload.efficientOrderOfItems;

      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );

      // Ganti daftar produk apabila koleksi ditemukan di daftar koleksi
      if (collectionIndex !== -1) {
        state.collectionsListDraft[collectionIndex].efficientOrderOfItems = [
          ...efficientOrderOfItems
        ];
      }

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    },
    // Create Collection UI Element
    createCollectionElement(state, action) {
      const collectionId = action.payload.collectionId;
      const UIBlockElement = action.payload.UIBlockElement;

      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );

      // Tambahkan elemen koleksi baru apabila koleksi ditemukan di daftar koleksi
      if (collectionIndex !== -1) {
        state.collectionsListDraft[
          collectionIndex
        ].discoveryListOfElements.push(deepCopy(UIBlockElement));
      }

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    },
    // Update Collection UI Element
    updateCollectionElement(state, action) {
      const collectionId = action.payload.collectionId;
      const UIBlockElement = action.payload.UIBlockElement;
      const UIBlockElementIndex = action.payload.UIBlockElementIndex;

      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );

      // Ubah elemen koleksi baru apabila koleksi ditemukan di daftar koleksi
      if (collectionIndex !== -1) {
        const list =
          state.collectionsListDraft[collectionIndex].discoveryListOfElements;
        state.collectionsListDraft[collectionIndex].discoveryListOfElements = [
          ...list.slice(0, UIBlockElementIndex),
          deepCopy(UIBlockElement),
          ...list.slice(UIBlockElementIndex + 1)
        ];
      }

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    },
    // Reorder Collection UI Element
    reorderCollectionElement(state, action) {
      const collectionId = action.payload.collectionId;
      const UIBlockElementIndex = action.payload.UIBlockElementIndex;
      const isMovedUp = action.payload.isMovedUp;

      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );
      if (
        collectionIndex === -1 ||
        UIBlockElementIndex < 0 ||
        UIBlockElementIndex >=
          state.collectionsListDraft[collectionIndex].discoveryListOfElements
            .length
      ) {
        return;
      }

      const list = state.collectionsListDraft[collectionIndex].discoveryListOfElements;

      const elementToMove = list[UIBlockElementIndex];
      const viewTypeIsMobile = elementToMove[`elementMobileCode`] && elementToMove[`elementMobileCode`] !== 'Empty';

      // Find new index for view. The new index is the first element before with the same viewType.
      var newIndex = UIBlockElementIndex;
      while (newIndex >= 0 && newIndex < state.collectionsListDraft[collectionIndex].discoveryListOfElements.length) {
        newIndex = isMovedUp ? newIndex - 1 : newIndex + 1;

        const nextEl = list[newIndex];
        const nextElIsMobile = nextEl[`elementMobileCode`] && nextEl[`elementMobileCode`] !== 'Empty';
        if ((viewTypeIsMobile && nextElIsMobile) || (!viewTypeIsMobile && !nextElIsMobile)) {
          break; // We found the new index. Now break.
        }

        // If the new index is out of range, we return since it means that the item is already top most / bottom most.
        if (newIndex < 0 || newIndex >= state.collectionsListDraft[collectionIndex].discoveryListOfElements.length) {
          return;
        }
      }

      const [elementToMove2] = list.splice(UIBlockElementIndex, 1);

      state.collectionsListDraft[collectionIndex].discoveryListOfElements = [
        ...list.slice(0, newIndex),
        elementToMove2,
        ...list.slice(newIndex)
      ];

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    },
    // Duplicate Collection UI Element
    duplicateCollectionElement(state, action) {
      const collectionId = action.payload.collectionId;
      const UIBlockElementIndex = action.payload.UIBlockElementIndex;

      // Tambah koleksi ke daftar koleksi yang diedit
      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );
      if (
        collectionIndex === -1 ||
        UIBlockElementIndex < 0 ||
        UIBlockElementIndex >=
          state.collectionsListDraft[collectionIndex].discoveryListOfElements
            .length
      ) {
        return;
      }

      const list = state.collectionsListDraft[collectionIndex].discoveryListOfElements;
      const elementToMove = JSON.parse(JSON.stringify(list[UIBlockElementIndex]));
      state.collectionsListDraft[collectionIndex].discoveryListOfElements = [
        ...list.slice(0, UIBlockElementIndex),
        elementToMove,
        ...list.slice(UIBlockElementIndex)
      ];

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    },
    // Delete Collection UI Element
    deleteCollectionElement(state, action) {
      const collectionId = action.payload.collectionId;
      const UIBlockElementIndex = action.payload.UIBlockElementIndex;

      // Tambah koleksi ke daftar koleksi yang diedit
      const collectionIndex = state.collectionsListDraft.findIndex(
        (collection) => collection.collectionId === collectionId
      );
      if (
        collectionIndex === -1 ||
        UIBlockElementIndex < 0 ||
        UIBlockElementIndex >=
          state.collectionsListDraft[collectionIndex].discoveryListOfElements
            .length
      ) {
        return;
      }

      const list =
        state.collectionsListDraft[collectionIndex].discoveryListOfElements;
      const [removedItem] = list.splice(UIBlockElementIndex, 1);

      state.collectionsListDraft[collectionIndex].discoveryListOfElements = [
        ...list
      ];

      // Tambah koleksi ke daftar koleksi yang diedit
      if (!state.editedCollection.has(collectionId)) {
        state.editedCollection.add(collectionId);
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProductsList.pending, (state, action) => {
        state.status = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductsList.fulfilled, (state, action) => {
        state.collectionsListDraft = deepCopy(action.payload.collections);
        state.discoveryCollectionsOrderDraft = deepCopy(
          action.payload.discoveryCollectionsOrder
        );
        state.discoveryCollectionsOrderDraftDict = deepCopy(
          action.payload.discoveryCollectionsOrderDict
        );

        // Drafts now mirror the server — reset all dirty tracking.
        state.editedCollection = new Set<string>();
        state.newCollections = new Set<string>();
        state.orderEdited = false;

        state.status = APIRequestStatus.Success;
      })
      .addCase(fetchProductsList.rejected, (state, action) => {
        state.status = APIRequestStatus.Failure;
      })
      .addCase(postUpdateCollection.pending, (state, action) => {
        state.statusPostUpdateCollection = APIRequestStatus.RequestInProgress;
      })
      .addCase(postUpdateCollection.fulfilled, (state, action) => {
        // Everything staged has been pushed — reset dirty tracking (the thunk
        // also refetches, which resets again once the fresh copy lands).
        state.editedCollection = new Set<string>();
        state.newCollections = new Set<string>();
        state.orderEdited = false;

        state.statusPostUpdateCollection = APIRequestStatus.Success;
      })
      .addCase(postUpdateCollection.rejected, (state, action) => {
        state.statusPostUpdateCollection = APIRequestStatus.Failure;
      })
  }
});

// Action creators are generated for each case reducer function
// export const { addProductToList } = productSlice.actions

export const {
  createCollection,
  reorderCollection,
  reorderCollectionDict,
  deleteCollection,
  updateCollectionProductList,
  updateCollectionEfficientOrderOrItems,
  createCollectionElement,
  updateCollectionElement,
  reorderCollectionElement,
  deleteCollectionElement,
  duplicateCollectionElement
} = slice.actions;

export default slice.reducer;
